import React, { useRef, useState, useEffect } from "react";
import { X, Upload, Loader2, Video, MessageCircle, HelpCircle, Plus as PlusIcon, Trash2, Music2, AtSign, Save, Mic, Square, Calendar as CalendarIcon, Users } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * StoryComposer — modal for adding to your story.
 *
 * Flow:
 *   1. User picks a video file (≤60 sec recommended for stories).
 *   2. Client uploads via `/api/uploads/video` (creator-gated on backend).
 *      If user is a viewer, we surface a "Become a creator" hint with a
 *      direct link to /apply-creator.
 *   3. On successful upload, POST `/api/community/stories` with the URL
 *      and caption — the story row is created with format=story.
 */
export default function StoryComposer({ onClose, onPosted, draft }) {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(draft?.video_url || null);
  const [uploadedUrl, setUploadedUrl] = useState(draft?.video_url || null);
  const [title, setTitle] = useState(draft?.title || "");
  const [duration, setDuration] = useState(draft?.duration_sec || 8);
  const [uploading, setUploading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [interactiveType, setInteractiveType] = useState(draft?.interactive?.type || null);
  const [question, setQuestion] = useState(draft?.interactive?.question || "");
  const [options, setOptions] = useState(draft?.interactive?.options?.length ? draft.interactive.options : ["", ""]);
  const [correctIndex, setCorrectIndex] = useState(draft?.interactive?.correct_index ?? 0);
  const [music, setMusic] = useState(draft?.music || null);
  const [musicPickerOpen, setMusicPickerOpen] = useState(false);
  const [audioOnly, setAudioOnly] = useState(draft?.audio_only || false);
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recSec, setRecSec] = useState(0);
  const [scheduledFor, setScheduledFor] = useState(draft?.scheduled_for || "");
  const [collabIds, setCollabIds] = useState(draft?.collab_with_user_ids || []);
  const [collabDetails, setCollabDetails] = useState([]);
  const [collabPickerOpen, setCollabPickerOpen] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [mentionAnchor, setMentionAnchor] = useState(null);   // { start, prefix }
  const [streak, setStreak] = useState(null); // { streak_days, posted_today }
  const titleRef = useRef(null);
  const mediaRecRef = useRef(null);
  const recTimerRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch the user's current posting streak so the composer can nudge them
  // to keep it alive ("🔥 5-day streak — post tonight to keep it going").
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get("/community/stories/my-streak")
      .then((r) => { if (!cancelled) setStreak(r.data || null); })
      .catch(() => { /* non-blocking */ });
    return () => { cancelled = true; };
  }, [user]);

  const canUpload = user && (user.role === "creator" || user.role === "premium_creator" || user.role === "admin");

  const onPick = (f) => {
    if (!f) return;
    if (!f.type.startsWith("video/")) { toast.error("Pick a video file"); return; }
    if (f.size > 500 * 1024 * 1024) { toast.error("Max 500 MB"); return; }
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreview(url);
    // Probe duration from a hidden video element.
    const v = document.createElement("video");
    v.preload = "metadata";
    v.src = url;
    v.onloadedmetadata = () => {
      const d = Math.min(15, Math.max(3, Math.round(v.duration || 8)));
      setDuration(d);
    };
  };

  // Voice-memo recording via MediaRecorder (audio/webm).
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => e.data && e.data.size > 0 && chunks.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        setRecordedBlob(blob);
        const url = URL.createObjectURL(blob);
        setRecordedUrl(url);
        setPreview(null);
        setAudioOnly(true);
        // Duration approximation
        setDuration(Math.min(30, Math.max(3, recSec || 10)));
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      mediaRecRef.current = mr;
      setRecording(true);
      setRecSec(0);
      recTimerRef.current = setInterval(() => setRecSec((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Mic permission denied");
    }
  };

  const stopRecording = () => {
    if (mediaRecRef.current) mediaRecRef.current.stop();
    setRecording(false);
    if (recTimerRef.current) clearInterval(recTimerRef.current);
  };

  // Mention autocomplete in title input.
  const onTitleChange = (e) => {
    const val = e.target.value;
    setTitle(val);
    const cur = e.target.selectionStart || val.length;
    const left = val.slice(0, cur);
    const m = left.match(/@([A-Za-z0-9_.\-]{0,40})$/);
    if (m) {
      setMentionAnchor({ start: cur - m[0].length, prefix: m[1] });
      if (m[1]) {
        api.get(`/users/mention-search?q=${encodeURIComponent(m[1])}&limit=6`)
          .then((r) => setMentionSuggestions(r.data?.results || []))
          .catch(() => setMentionSuggestions([]));
      } else {
        setMentionSuggestions([]);
      }
    } else {
      setMentionAnchor(null);
      setMentionSuggestions([]);
    }
  };

  const applyMention = (u) => {
    if (!mentionAnchor) return;
    const handle = (u.name || "").replace(/\s+/g, "");
    const before = title.slice(0, mentionAnchor.start);
    const after = title.slice(mentionAnchor.start + 1 + (mentionAnchor.prefix?.length || 0));
    const next = `${before}@${handle} ${after}`.replace(/\s{2,}/g, " ");
    setTitle(next);
    setMentionSuggestions([]);
    setMentionAnchor(null);
    setTimeout(() => titleRef.current && titleRef.current.focus(), 0);
  };

  // Collab resolution: we keep full stubs (name/picture/user_id) in state
  // so there's no second lookup needed for chip rendering.
  useEffect(() => { /* collabDetails is kept in sync by the picker */ }, [collabIds]);

  const buildInteractive = () => {
    if (!interactiveType) return null;
    const q = question.trim();
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (!q) { toast.error("Question required"); return undefined; }
    if (opts.length < 2) { toast.error("Need at least 2 options"); return undefined; }
    const obj = { type: interactiveType, question: q, options: opts };
    if (interactiveType === "quiz") {
      if (correctIndex < 0 || correctIndex >= opts.length) { toast.error("Pick the correct answer"); return undefined; }
      obj.correct_index = correctIndex;
    }
    return obj;
  };

  const doUpload = async () => {
    // Audio recording — upload the blob as a file
    if (audioOnly && recordedBlob) {
      const fd = new FormData();
      fd.append("file", new File([recordedBlob], "voice.webm", { type: "audio/webm" }));
      const up = await api.post("/uploads/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = up.data?.storage_path?.startsWith("http")
        ? up.data.storage_path
        : `${process.env.REACT_APP_BACKEND_URL || ""}/${String(up.data?.storage_path || "").replace(/^\/+/, "")}`;
      setUploadedUrl(url);
      return url;
    }
    // When editing a draft we may already have a URL
    if (uploadedUrl && !file) return uploadedUrl;
    if (!file) { toast.error("Pick a video first"); return null; }
    const fd = new FormData();
    fd.append("file", file);
    const up = await api.post("/uploads/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
    const url = up.data?.storage_path?.startsWith("http")
      ? up.data.storage_path
      : `${process.env.REACT_APP_BACKEND_URL || ""}/${String(up.data?.storage_path || "").replace(/^\/+/, "")}`;
    setUploadedUrl(url);
    return url;
  };

  const submit = async () => {
    if (!canUpload) { toast.error("Only creators can post stories right now"); return; }
    const interactive = buildInteractive();
    if (interactive === undefined) return;   // validation failed
    setUploading(true);
    try {
      const url = await doUpload();
      if (!url) return;
      const body = { video_url: url, title: title.trim(), duration_sec: duration };
      if (interactive) body.interactive = interactive;
      if (music) body.music = music;
      if (audioOnly) body.audio_only = true;
      if (collabIds.length) body.collab_with_user_ids = collabIds;
      if (draft?.id) {
        // Publish via drafts publish endpoint (idempotent)
        // First patch the draft with any local changes
        await api.patch(`/creator/story-drafts/${draft.id}`, {
          video_url: url, title: title.trim(), duration_sec: duration,
          interactive: interactive || null,
          music: music || null,
          audio_only: audioOnly,
          collab_with_user_ids: collabIds,
          scheduled_for: scheduledFor || null,
        });
        await api.post(`/creator/story-drafts/${draft.id}/publish`);
      } else {
        await api.post("/community/stories", body);
      }
      toast.success("Story posted");
      onPosted && onPosted();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const saveDraft = async () => {
    if (!canUpload) { toast.error("Only creators can save drafts"); return; }
    const interactive = buildInteractive();
    if (interactive === undefined) return;
    setSavingDraft(true);
    try {
      const url = await doUpload();   // may be null if no file & no uploaded yet
      const body = {
        title: title.trim(),
        duration_sec: duration,
        video_url: url || "",
        interactive: interactive || null,
        music: music || null,
        audio_only: audioOnly,
        collab_with_user_ids: collabIds,
        scheduled_for: scheduledFor || null,
      };
      if (draft?.id) {
        await api.patch(`/creator/story-drafts/${draft.id}`, body);
        toast.success("Draft updated");
      } else {
        await api.post("/creator/story-drafts", body);
        toast.success("Saved to drafts");
      }
      onPosted && onPosted();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingDraft(false); }
  };

  return (
    <div data-testid="story-composer" className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/10 rounded-2xl shadow-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-black text-2xl text-white">Add to your story</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {!canUpload ? (
          <div className="space-y-3">
            <div className="text-white/70 text-sm">Stories are a creator feature — bring your work onto the Tap IN network.</div>
            <a
              href="/apply-creator"
              data-testid="story-apply-creator"
              className="block w-full text-center bg-tv-accent hover:bg-tv-accent/90 text-black font-bold px-4 py-3 rounded-full"
            >Apply as a creator</a>
            <button onClick={onClose} className="w-full text-white/60 hover:text-white text-sm py-2">Not now</button>
          </div>
        ) : (
          <>
            {/* Mode toggle: Video vs Voice memo */}
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                data-testid="story-mode-video"
                onClick={() => { setAudioOnly(false); setRecordedBlob(null); setRecordedUrl(null); }}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold py-2 rounded-full ${!audioOnly ? "bg-tv-accent text-black" : "bg-white/10 text-white/70 hover:bg-white/15"}`}
              ><Video className="w-3.5 h-3.5" /> Video</button>
              <button
                type="button"
                data-testid="story-mode-voice"
                onClick={() => { setAudioOnly(true); setFile(null); setPreview(null); setUploadedUrl(null); }}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold py-2 rounded-full ${audioOnly ? "bg-tv-accent text-black" : "bg-white/10 text-white/70 hover:bg-white/15"}`}
              ><Mic className="w-3.5 h-3.5" /> Voice memo</button>
            </div>

            {audioOnly ? (
              <div data-testid="story-voice-recorder" className="aspect-[9/16] max-h-[50vh] bg-gradient-to-br from-fuchsia-500/30 via-orange-400/20 to-yellow-400/20 border-2 border-white/10 rounded-2xl mx-auto flex flex-col items-center justify-center overflow-hidden relative p-6">
                {/* Animated waveform */}
                <div className="flex items-end gap-1 h-16 mb-4" aria-hidden>
                  {Array.from({ length: 24 }).map((_, i) => (
                    <div
                      key={i}
                      className="w-1.5 rounded-full bg-white/80"
                      style={{
                        height: `${20 + Math.sin((i + (recording ? recSec : 0)) * 0.7) * 20 + (recording ? Math.random() * 25 : 10)}px`,
                        transition: "height 200ms ease-out",
                      }}
                    />
                  ))}
                </div>
                {recordedUrl ? (
                  <div className="w-full space-y-3">
                    <audio src={recordedUrl} controls className="w-full" />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => { setRecordedBlob(null); setRecordedUrl(null); setUploadedUrl(null); setRecSec(0); }}
                        className="flex-1 bg-white/10 hover:bg-white/15 text-white font-bold py-2 rounded-full text-sm"
                      >Re-record</button>
                    </div>
                  </div>
                ) : recording ? (
                  <>
                    <div className="text-white font-display font-black text-4xl tabular-nums">{Math.floor(recSec / 60)}:{String(recSec % 60).padStart(2, "0")}</div>
                    <div className="text-white/70 text-xs uppercase tracking-[0.3em] mt-2">Recording…</div>
                    <button
                      type="button"
                      data-testid="story-voice-stop"
                      onClick={stopRecording}
                      className="mt-4 bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-3 rounded-full inline-flex items-center gap-2"
                    ><Square className="w-4 h-4" fill="currentColor" /> Stop</button>
                  </>
                ) : (
                  <>
                    <div className="text-white/70 text-sm mb-4 text-center">Tap to record a voice memo<br/><span className="text-white/40 text-xs">3-30 seconds</span></div>
                    <button
                      type="button"
                      data-testid="story-voice-start"
                      onClick={startRecording}
                      className="bg-red-500 hover:bg-red-600 text-white font-bold p-5 rounded-full"
                    ><Mic className="w-6 h-6" /></button>
                  </>
                )}
              </div>
            ) : (
              <div
                data-testid="story-dropzone"
                className="aspect-[9/16] max-h-[50vh] bg-black/40 border-2 border-dashed border-white/20 rounded-2xl mx-auto flex items-center justify-center overflow-hidden relative"
                onClick={() => inputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
              >
                {preview ? (
                  <video src={preview} controls playsInline className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center text-white/60 p-6">
                    <Video className="w-10 h-10 mx-auto mb-2 text-white/40" />
                    <div className="text-sm font-bold text-white">Tap to choose a video</div>
                    <div className="text-xs mt-1">9:16 works best · 3-15 sec</div>
                  </div>
                )}
                <input
                  ref={inputRef}
                  data-testid="story-file-input"
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={(e) => onPick(e.target.files?.[0])}
                />
              </div>
            )}

            <div className="relative">
              <input
                ref={titleRef}
                data-testid="story-title-input"
                value={title}
                onChange={onTitleChange}
                maxLength={120}
                placeholder="Add a caption (optional) · use @name to mention"
                className="w-full mt-4 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent"
              />
              {mentionAnchor && mentionSuggestions.length > 0 && (
                <div data-testid="mention-suggestions" className="absolute z-30 mt-1 w-full max-h-48 overflow-y-auto bg-[#1a1a1a] border border-white/15 rounded-lg shadow-xl">
                  {mentionSuggestions.map((u) => (
                    <button
                      key={u.user_id}
                      data-testid={`mention-suggestion-${u.user_id}`}
                      type="button"
                      onClick={() => applyMention(u)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-white/10 text-left"
                    >
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-white/60 text-xs font-bold">{(u.name || "?")[0]}</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm font-bold truncate">{u.name}</div>
                      </div>
                      {u.is_top_creator && <span className="text-[8px] bg-tv-accent text-black font-black px-1 py-0.5 rounded uppercase">Top</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {title.match(/@([A-Za-z0-9_][A-Za-z0-9_.\-]{1,40})/) && (
              <div data-testid="story-mention-hint" className="mt-1.5 flex items-center gap-1 text-xs text-tv-accent font-semibold">
                <AtSign className="w-3 h-3" /> Mentioned users will get a DM + push.
              </div>
            )}

            {/* Music sticker picker */}
            <div className="mt-4 bg-black/20 border border-white/10 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <Music2 className="w-4 h-4 text-tv-accent" />
                <div className="text-xs text-white/80 uppercase tracking-wider font-bold">Music sticker (optional)</div>
                {music && (
                  <button
                    data-testid="story-music-clear"
                    onClick={() => setMusic(null)}
                    className="ml-auto text-[10px] text-white/50 hover:text-red-300 uppercase tracking-wider font-bold"
                  >Clear</button>
                )}
              </div>
              {music ? (
                <div className="flex items-center gap-3 bg-black/40 rounded-lg p-2">
                  {music.album_art && <img src={music.album_art} alt="" className="w-10 h-10 rounded object-cover" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-bold truncate">{music.title}</div>
                    <div className="text-white/60 text-xs truncate">{music.artist}</div>
                  </div>
                  {music.preview_url && <audio src={music.preview_url} controls className="h-6 max-w-[140px]" />}
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="story-music-picker-btn"
                  onClick={() => setMusicPickerOpen(true)}
                  className="w-full inline-flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/15 text-white text-sm font-bold py-2 rounded-full"
                ><Music2 className="w-4 h-4" /> Pick a track</button>
              )}
            </div>

            {/* Interactive sticker picker */}
            <div className="mt-4 bg-black/20 border border-white/10 rounded-xl p-3">
              <div className="text-xs text-white/60 mb-2 uppercase tracking-wider font-bold">Sticker (optional)</div>
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  data-testid="story-composer-type-none"
                  onClick={() => setInteractiveType(null)}
                  className={`flex-1 text-xs font-bold py-1.5 rounded-full ${interactiveType === null ? "bg-white text-black" : "bg-white/10 text-white/70 hover:bg-white/15"}`}
                >None</button>
                <button
                  type="button"
                  data-testid="story-composer-type-poll"
                  onClick={() => setInteractiveType("poll")}
                  className={`flex-1 inline-flex items-center justify-center gap-1 text-xs font-bold py-1.5 rounded-full ${interactiveType === "poll" ? "bg-tv-accent text-black" : "bg-white/10 text-white/70 hover:bg-white/15"}`}
                ><MessageCircle className="w-3.5 h-3.5" /> Poll</button>
                <button
                  type="button"
                  data-testid="story-composer-type-quiz"
                  onClick={() => setInteractiveType("quiz")}
                  className={`flex-1 inline-flex items-center justify-center gap-1 text-xs font-bold py-1.5 rounded-full ${interactiveType === "quiz" ? "bg-tv-accent text-black" : "bg-white/10 text-white/70 hover:bg-white/15"}`}
                ><HelpCircle className="w-3.5 h-3.5" /> Quiz</button>
              </div>
              {interactiveType && (
                <div className="space-y-2">
                  <input
                    data-testid="story-composer-question"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    maxLength={160}
                    placeholder={interactiveType === "quiz" ? "Quiz question" : "Poll question"}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent"
                  />
                  {options.map((o, i) => (
                    <div key={i} className="flex items-center gap-2">
                      {interactiveType === "quiz" && (
                        <button
                          type="button"
                          data-testid={`story-composer-correct-${i}`}
                          onClick={() => setCorrectIndex(i)}
                          className={`w-5 h-5 rounded-full border-2 shrink-0 ${correctIndex === i ? "bg-tv-accent border-tv-accent" : "border-white/30"}`}
                          title={correctIndex === i ? "Correct answer" : "Mark as correct"}
                        />
                      )}
                      <input
                        data-testid={`story-composer-option-${i}`}
                        value={o}
                        onChange={(e) => setOptions((arr) => arr.map((x, j) => j === i ? e.target.value : x))}
                        maxLength={80}
                        placeholder={`Option ${i + 1}`}
                        className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent"
                      />
                      {options.length > 2 && (
                        <button type="button" onClick={() => { setOptions((arr) => arr.filter((_, j) => j !== i)); if (correctIndex >= options.length - 1) setCorrectIndex(0); }} className="p-1 text-white/50 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  ))}
                  {options.length < 4 && (
                    <button
                      type="button"
                      data-testid="story-composer-add-option"
                      onClick={() => setOptions((arr) => [...arr, ""])}
                      className="text-xs text-tv-accent hover:text-yellow-300 font-bold inline-flex items-center gap-1"
                    ><PlusIcon className="w-3.5 h-3.5" /> Add option</button>
                  )}
                </div>
              )}
            </div>

            {/* Collab + schedule row */}
            <div className="mt-4 bg-black/20 border border-white/10 rounded-xl p-3 space-y-3">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-tv-accent" />
                  <div className="text-xs text-white/80 uppercase tracking-wider font-bold">Collaborators (optional)</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {collabDetails.map((u) => (
                    <span
                      key={u.user_id}
                      data-testid={`collab-chip-${u.user_id}`}
                      className="inline-flex items-center gap-1.5 bg-white/10 text-white text-xs font-bold pl-1 pr-2 py-0.5 rounded-full"
                    >
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-5 h-5 rounded-full object-cover" />
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-white/20" />
                      )}
                      {u.name}
                      <button
                        type="button"
                        onClick={() => {
                          setCollabIds((ids) => ids.filter((x) => x !== u.user_id));
                          setCollabDetails((arr) => arr.filter((x) => x.user_id !== u.user_id));
                        }}
                        className="ml-0.5 text-white/50 hover:text-red-300"
                      ><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {collabIds.length < 3 && (
                    <button
                      type="button"
                      data-testid="collab-add-btn"
                      onClick={() => setCollabPickerOpen(true)}
                      className="inline-flex items-center gap-1 text-xs bg-white/10 hover:bg-white/15 text-white font-bold px-3 py-1 rounded-full"
                    ><PlusIcon className="w-3 h-3" /> Add collaborator</button>
                  )}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CalendarIcon className="w-4 h-4 text-tv-accent" />
                  <div className="text-xs text-white/80 uppercase tracking-wider font-bold">Schedule (UTC, optional)</div>
                </div>
                {streak && streak.streak_days >= 1 && (
                  <div
                    data-testid="story-streak-banner"
                    className="mb-2 bg-gradient-to-r from-orange-500/20 via-amber-500/20 to-yellow-500/20 border border-amber-400/40 text-amber-100 text-xs font-semibold px-3 py-2 rounded-lg flex items-center gap-2"
                  >
                    <span className="text-base leading-none">🔥</span>
                    <span data-testid="story-streak-text">
                      {streak.posted_today
                        ? `You're on a ${streak.streak_days}-day streak — post tomorrow to keep it going.`
                        : `Post today to extend your ${streak.streak_days}-day streak. Top creators average 4× more reach when posting daily.`}
                    </span>
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {[
                    { label: "Tonight 7 PM", hour: 19 },
                    { label: "Tonight 9 PM", hour: 21 },
                    { label: "Tomorrow 12 PM", hour: 12, addDay: true },
                  ].map(({ label, hour, addDay }) => {
                    const target = new Date();
                    if (addDay || target.getHours() >= hour) {
                      target.setDate(target.getDate() + 1);
                    }
                    target.setHours(hour, 0, 0, 0);
                    const targetIso = target.toISOString();
                    const isActive = scheduledFor === targetIso;
                    return (
                      <button
                        key={label}
                        type="button"
                        data-testid={`schedule-preset-${hour}${addDay ? "-tom" : ""}`}
                        onClick={() => setScheduledFor(isActive ? "" : targetIso)}
                        className={`text-[11px] font-bold px-3 py-1 rounded-full transition-colors ${
                          isActive
                            ? "bg-tv-accent text-white"
                            : "bg-white/10 hover:bg-white/15 text-white/90"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <input
                  data-testid="story-schedule-input"
                  type="datetime-local"
                  value={scheduledFor ? scheduledFor.slice(0, 16) : ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setScheduledFor(v ? new Date(v + "Z").toISOString() : "");
                  }}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent"
                />
                {scheduledFor && (
                  <div className="text-[11px] text-tv-accent font-semibold mt-1">
                    Will auto-publish at {new Date(scheduledFor).toLocaleString()}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={onClose}
                className="flex-1 bg-white/10 hover:bg-white/15 text-white font-bold py-2.5 rounded-full"
              >Cancel</button>
              <button
                data-testid="story-save-draft-btn"
                onClick={saveDraft}
                disabled={savingDraft || uploading}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-white/15 disabled:opacity-40 hover:bg-white/25 text-white font-bold py-2.5 rounded-full"
              >
                {savingDraft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {savingDraft ? "Saving…" : (draft?.id ? "Update draft" : "Save draft")}
              </button>
              <button
                data-testid="story-post-btn"
                onClick={submit}
                disabled={uploading || savingDraft || (!file && !uploadedUrl)}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-tv-accent disabled:opacity-40 hover:bg-tv-accent/90 text-black font-bold py-2.5 rounded-full"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Posting…" : (draft?.id ? "Publish" : "Post story")}
              </button>
            </div>

            {musicPickerOpen && (
              <MusicPicker
                onClose={() => setMusicPickerOpen(false)}
                onPick={(track) => { setMusic(track); setMusicPickerOpen(false); }}
              />
            )}
            {collabPickerOpen && (
              <CollabPicker
                onClose={() => setCollabPickerOpen(false)}
                onPick={(u) => {
                  if (!collabIds.includes(u.user_id)) {
                    setCollabIds((ids) => [...ids, u.user_id]);
                    setCollabDetails((arr) => [...arr, u]);
                  }
                  setCollabPickerOpen(false);
                }}
                excludeIds={collabIds}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}


function MusicPicker({ onClose, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/music/search?q=${encodeURIComponent(q)}&limit=20`);
        setResults(data?.results || []);
      } catch { setResults([]); }
      finally { setBusy(false); }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  return (
    <div data-testid="story-music-picker" className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#111] border border-white/10 rounded-2xl shadow-2xl p-5 w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-black text-xl text-white flex items-center gap-2"><Music2 className="w-5 h-5 text-tv-accent" /> Pick a track</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <input
          data-testid="story-music-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
          placeholder="Song, artist, album…"
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent"
        />
        <div className="mt-3 flex-1 overflow-y-auto space-y-1.5">
          {busy && <div className="text-white/50 text-sm text-center py-6">Searching…</div>}
          {!busy && q && results.length === 0 && <div className="text-white/50 text-sm text-center py-6">No matches.</div>}
          {results.map((t) => (
            <button
              key={t.id || `${t.title}-${t.artist}`}
              data-testid={`story-music-result-${t.id || "tr"}`}
              onClick={() => onPick(t)}
              className="w-full flex items-center gap-3 bg-white/5 hover:bg-white/10 rounded-lg px-3 py-2 text-left"
            >
              {t.album_art && <img src={t.album_art} alt="" className="w-10 h-10 rounded object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold text-sm truncate">{t.title}</div>
                <div className="text-white/60 text-xs truncate">{t.artist}</div>
              </div>
              {t.preview_url && <span className="text-[10px] text-green-400 font-bold">30s</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function CollabPicker({ onClose, onPick, excludeIds = [] }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/users/mention-search?q=${encodeURIComponent(q)}&limit=10`);
        setResults((data?.results || []).filter((u) => !excludeIds.includes(u.user_id)));
      } catch { setResults([]); }
    }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, excludeIds]);

  return (
    <div data-testid="collab-picker" className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#111] border border-white/10 rounded-2xl shadow-2xl p-5 w-full max-w-md max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-black text-xl text-white flex items-center gap-2"><Users className="w-5 h-5 text-tv-accent" /> Tag a collaborator</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <input
          data-testid="collab-picker-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
          placeholder="Creator name…"
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent"
        />
        <div className="mt-3 flex-1 overflow-y-auto space-y-1">
          {q && results.length === 0 && <div className="text-white/50 text-sm text-center py-6">No matches.</div>}
          {results.map((u) => (
            <button
              key={u.user_id}
              data-testid={`collab-pick-${u.user_id}`}
              onClick={() => onPick(u)}
              className="w-full flex items-center gap-2 bg-white/5 hover:bg-white/10 rounded-lg px-3 py-2 text-left"
            >
              {u.picture ? (
                <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 font-bold">{(u.name || "?")[0]}</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold text-sm truncate">{u.name}</div>
              </div>
              {u.is_top_creator && <span className="text-[9px] bg-tv-accent text-black font-black px-1.5 py-0.5 rounded uppercase">Top</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

